import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/comics';

const platformStats = [
  { value: '12,580+', label: '漫画总数', icon: 'ri-book-2-line', color: 'bg-primary-100 text-primary-600' },
  { value: '356,000+', label: '章节数量', icon: 'ri-stack-line', color: 'bg-accent-100 text-accent-600' },
  { value: '280万+', label: '活跃读者', icon: 'ri-user-heart-line', color: 'bg-secondary-100 text-secondary-600' },
  { value: '86+', label: '每日更新', icon: 'ri-refresh-line', color: 'bg-primary-100 text-primary-600' },
  { value: '4.8', label: '平均评分', icon: 'ri-star-line', color: 'bg-accent-100 text-accent-600' },
  { value: '6年', label: '服务时间', icon: 'ri-calendar-check-line', color: 'bg-secondary-100 text-secondary-600' },
];

const milestones = [
  { year: '2018', title: '平台创立', description: '韩漫之家正式成立，开始为读者提供韩漫在线阅读服务。' },
  { year: '2019', title: '突破百万用户', description: '平台注册用户突破100万，日活用户达到10万。' },
  { year: '2020', title: '智能加载技术上线', description: '首创下拉式智能加载阅读模式，大幅提升阅读体验。' },
  { year: '2021', title: '移动端优化', description: '全面优化移动端体验，推出响应式设计，支持多设备无缝阅读。' },
  { year: '2022', title: '内容突破万部', description: '平台收录漫画突破1万部，涵盖恋爱、奇幻、动作、悬疑等多种类型。' },
  { year: '2024', title: '全新改版', description: '平台全面升级改版，推出更现代的设计和更流畅的交互体验。' },
  { year: '2026', title: '持续增长', description: '服务读者突破280万，每日更新86+部作品，成为国内领先的韩漫阅读平台。' },
];

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于韩漫之家',
          description: '了解韩漫之家品牌故事、用户口碑与联系方式。作为专业韩漫网站，我们致力于为用户提供优质的免费下拉式漫画阅读体验。',
          url: 'http://rfeff.com/about',
          about: {
            '@type': 'Organization',
            name: '韩漫之家',
            foundingDate: '2018',
            description: aboutInfo.introduction,
            url: 'http://rfeff.com/',
            contactPoint: {
              '@type': 'ContactPoint',
              email: aboutInfo.contact.email,
              telephone: aboutInfo.contact.phone,
              contactType: 'customer service',
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: aboutInfo.contact.address,
              addressCountry: 'CN',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              reviewCount: '12500',
            },
          },
          isPartOf: {
            '@type': 'WebSite',
            name: '韩漫之家',
            url: 'http://rfeff.com/',
          },
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: aboutInfo.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        })}
      </script>

      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero / 介绍 */}
          <section className="py-10 md:py-14">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950">
                关于韩漫之家
              </h1>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                {aboutInfo.introduction}
              </p>
            </div>
          </section>

          {/* 平台数据统计 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {platformStats.map((stat, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-5 text-center border border-background-200/70">
                  <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-lg ${stat.color} mb-3`}>
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* 使命愿景 */}
          <section className="py-10 border-t border-background-200/70">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-focus-2-line text-lg"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  我们的使命
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-line text-lg"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  我们的愿景
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.vision}
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-6">
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-calendar-line text-lg"></i>
                </div>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                  成立时间
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.brandName}成立于{aboutInfo.foundedYear}年，至今已服务数百万韩漫爱好者。
                </p>
              </div>
            </div>
          </section>

          {/* 核心价值观 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.values.map((value, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <span className="text-sm font-bold">{index + 1}</span>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 发展历程 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              发展历程
            </h2>
            <div className="relative max-w-3xl">
              <div className="absolute left-4 md:left-0 md:right-0 md:mx-auto top-0 bottom-0 w-0.5 bg-background-200"></div>
              <div className="space-y-8">
                {milestones.map((m, index) => (
                  <div key={index} className="relative flex items-start gap-6 md:gap-8">
                    <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold relative z-10 ring-4 ring-background-50">
                      {m.year.slice(-2)}
                    </div>
                    <div className="bg-background-100 rounded-xl p-5 border border-background-200/70 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                          {m.year}
                        </span>
                        <h3 className="text-sm font-heading font-bold text-foreground-900">
                          {m.title}
                        </h3>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed mt-1">
                        {m.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 用户口碑 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {aboutInfo.testimonials.map((testimonial, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <div key={star} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{testimonial.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-600">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{testimonial.name}</p>
                      <p className="text-xs text-foreground-500">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* 常见问题 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              常见问题
            </h2>
            <div className="space-y-4 max-w-3xl">
              {aboutInfo.faqs.map((faq, index) => (
                <details key={index} className="bg-background-100 rounded-xl border border-background-200/70 group">
                  <summary className="px-6 py-4 cursor-pointer flex items-center justify-between text-foreground-800 hover:text-primary-500 transition-colors">
                    <h3 className="text-sm font-semibold pr-4">{faq.question}</h3>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-xs text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* 联系我们 */}
          <section className="py-10 border-t border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-phone-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">联系电话</h3>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">公司地址</h3>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">微信公众号</h3>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">微博</h3>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-bilibili-line"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800">B站</h3>
                    <p className="text-sm text-foreground-500">韩漫之家官方</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 商务合作 */}
          <section className="py-10 border-t border-background-200/70 mb-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              商务合作
            </h2>
            <div className="bg-primary-50 rounded-xl p-6 md:p-8 border border-primary-200/50">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-2">
                    版权合作 · 广告投放 · 品牌推广
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed max-w-xl">
                    韩漫之家拥有280万+活跃读者，是韩漫内容推广的优质渠道。欢迎漫画版权方、广告主及合作伙伴联系我们，共同开拓韩漫市场。
                  </p>
                </div>
                <div className="shrink-0">
                  <a
                    href={`mailto:${aboutInfo.contact.email}`}
                    className="whitespace-nowrap inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-send-line mr-2"></i>
                    联系我们
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}